// components/ViewOnlyPortfolio.js
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../config/axiosConfig';
import { Container, CircularProgress, Alert, Box, Typography, Button } from '@mui/material';
import ViewOnlyPortfolioTable from './ViewOnlyPortfolioTable';
import ViewOnlyPortfolioSummary from './ViewOnlyPortfolioSummary';

const ViewOnlyPortfolio = ({ user }) => {
  const [portfolio, setPortfolio] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [sectorAllocation, setSectorAllocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);

  // Extract parameters directly from the URL
  const { nickname, portfolioName } = useParams();

  useEffect(() => {
    const fetchPortfolio = async () => {
      if (!nickname || !portfolioName) {
        setError('Invalid portfolio URL');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.get(`/api/shared/portfolio/${nickname}/${portfolioName}/holdings`);

        setPortfolio(response.data.portfolio);
        setHoldings(response.data.holdings);
        setSummary(response.data.summary);
        setSectorAllocation(response.data.sectorAllocation);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
        setError('This portfolio does not exist or is not available for viewing');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPortfolio();
  }, [nickname, portfolioName]);

  useEffect(() => {
    const checkIfFollowing = async () => {
      if (!user || !portfolio) return;

      try {
        const response = await axios.get(`/api/shared/portfolio/${portfolio._id}/following-status`);
        setIsFollowing(response.data.following);
      } catch (error) {
        console.error('Error checking follow status:', error);
      }
    };

    checkIfFollowing();
  }, [user, portfolio]);

  const handleFollowToggle = async () => {
    if (!user) {
      window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
      return;
    }

    if (!portfolio) return;

    try {
      const response = await axios.post(`/api/shared/portfolio/${portfolio._id}/follow`);
      setIsFollowing(response.data.following);
    } catch (error) {
      console.error('Error toggling follow status:', error);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Container>
    );
  }

  if (!portfolio) {
    return (
      <Container maxWidth="xl">
        <Alert severity="info" sx={{ mt: 2 }}>Portfolio not found</Alert>
      </Container>
    );
  }

  // Determine view mode using inline logic
  const isFullView =
    portfolio.privacyLevel !== 'private' &&
    portfolio.sharedViewMode === 'full';

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
        <Typography variant="h5">
          {portfolio.name}
          {portfolio.publicNickname && ` by ${portfolio.publicNickname}`}
        </Typography>

        {portfolio.privacyLevel === 'public' && user && (
          <Button
            variant="contained"
            color={isFollowing ? "secondary" : "primary"}
            onClick={handleFollowToggle}
          >
            {isFollowing ? "Unfollow" : "Follow"}
          </Button>
        )}
      </Box>

      {/* Show summary only when appropriate */}
      {isFullView && holdings.length > 0 && (
        <ViewOnlyPortfolioSummary
          holdings={holdings}
          portfolioInfo={portfolio}
          summary={summary}
          sectorAllocation={sectorAllocation}
        />
      )}

      <ViewOnlyPortfolioTable
        holdings={holdings}
        privacyLevel={isFullView ? 'full' : 'limited'}
        baseCurrency={portfolio.baseCurrency}
      />
    </Container>
  );
};

export default ViewOnlyPortfolio;